import React, { useState } from 'react';
import { LandingMiniCard } from './LandingMiniCard';

interface SearchResult {
  score: number;
  content: string;
  metadata: {
    id: number;
    title?: string;
    keywords?: string;
    full_text?: string;
    utech_title?: string;
    common_email?: string;
    created_date: string;
    utech_summary?: string;
    ip_information?: string;
    utech_keywords?: string;
    university_name?: string;
    reference_number?: string;
    development_stage?: string;
    page_updated_date?: string;
    page_accessed_date?: string;
    researcher_details?: string;
    university_country?: string;
    industry_categories?: string;
    utech_technology_id?: string;
    utech_university_id?: string;
    tech_manager_default?: string;
    tech_manager_details?: string;
    university_portal_url?: string;
    researcher_ids_details?: string;
    tech_manager_ids_details?: string;
    university_technology_url?: string;
    utech_industry_categories?: string;
    // Researcher-specific fields
    researcher_name?: string;
    utech_researcher_id?: string;
    // IP and Publications fields
    ip_summary?: string;
    publications_summary?: string;
    // TRL reasoning field
    // OLD: interpreted_trl_reasoning?: string;
    calculated_trl_statement?: string;
  };
  algorithm: string;
  percentage: number;
  source_type: 'technology' | 'researcher';
}

interface LandingChatSearchResultsProps {
  searchResults: SearchResult[];
  onReadMore: () => void;
}

export const LandingChatSearchResults: React.FC<LandingChatSearchResultsProps> = ({ 
  searchResults,
  onReadMore
}) => {
  // Sort search results by relevance score (highest to lowest)
  const sortedSearchResults = React.useMemo(() => {
    return [...searchResults].sort((a, b) => {
      const scoreA = a.percentage || a.score * 100 || 0;
      const scoreB = b.percentage || b.score * 100 || 0;
      return scoreB - scoreA; // Highest to lowest
    });
  }, [searchResults]);

  const [closedCards, setClosedCards] = useState<Set<number>>(new Set());
  const [showAllResults, setShowAllResults] = useState(false);
  const [initialDisplayCount] = useState(5); // Show 5 results initially

  // Reset show more state when search results change
  React.useEffect(() => {
    setShowAllResults(false);
  }, [searchResults]);

  const closeCard = (cardId: number) => {
    setClosedCards(prev => {
      const newSet = new Set(prev);
      newSet.add(cardId);
      return newSet;
    });
  };

  const renderTechnologyCard = (result: SearchResult, index: number) => {
    const metadata = result.metadata;
    
    // Skip if card was closed
    if (closedCards.has(index)) {
      return null;
    }
    
    let trlDisplay = 'TRL Not Disclosed';
    try {
      const devStage = JSON.parse(metadata.development_stage || '{}');
      // Extract numeric TRL from calculated_trl using regex
      const calculatedTrl = devStage.calculated_trl;
      if (calculatedTrl) {
        // Check if explicitly "Not Disclosed"
        const calcTrlStr = String(calculatedTrl).toLowerCase();
        if (calcTrlStr.includes('not disclosed') || calcTrlStr.includes('not available')) {
          trlDisplay = 'TRL Not Disclosed';
        } else {
          const trlMatch = String(calculatedTrl).match(/(\d+)/);
          if (trlMatch) {
            // Check confidence score
            const confidenceScore = devStage.confidence_score;
            if (confidenceScore !== undefined && confidenceScore !== null) {
              const confValue = parseInt(String(confidenceScore));
              if (confValue >= 3) {
                trlDisplay = `TRL ${trlMatch[1]}`;
              }
              // else keep "TRL Not Disclosed"
            } else {
              // No confidence score, use TRL value
              trlDisplay = `TRL ${trlMatch[1]}`;
            }
          }
        }
      }
    } catch (e) {
      trlDisplay = 'TRL Not Disclosed';
    }

    return (
      <div key={index} className="flex-shrink-0">
        <LandingMiniCard
          type="technology"
          title={metadata.utech_title || metadata.title || 'Unknown Technology'}
          location={`${metadata.university_name || 'Unknown'}, ${metadata.university_country || 'Unknown'}`}
          detail={trlDisplay}
          relevanceScore={result.algorithm === 'sql_broad_query' ? 'Keyword match' : Math.round(result.percentage)}
          onReadMore={onReadMore}
        />
      </div>
    );
  };

  const renderResearcherCard = (result: SearchResult, index: number) => {
    const metadata = result.metadata;
    
    // Skip if card was closed
    if (closedCards.has(index)) {
      return null;
    }
    
    const researcherName = metadata.researcher_name || 'Unknown Researcher';
    const institution = metadata.university_name || '';
    const location = metadata.university_country || '';

    return (
      <div key={index} className="flex-shrink-0">
        <LandingMiniCard
          type="researcher"
          title={researcherName}
          location={[institution, location].filter(Boolean).join(', ')}
          detail="1 research area"
          relevanceScore={result.algorithm === 'sql_broad_query' ? 'Keyword match' : Math.round(result.percentage)}
          onReadMore={onReadMore}
        />
      </div>
    );
  };

  if (!searchResults || searchResults.length === 0) {
    return null;
  }

  // Filter out closed cards for count (using sorted results)
  const visibleResults = sortedSearchResults.filter((_, index) => !closedCards.has(index));
  
  if (visibleResults.length === 0) {
    return null;
  }

  // Determine which results to display based on showAllResults state
  const displayedResults = showAllResults 
    ? visibleResults 
    : visibleResults.slice(0, initialDisplayCount);
  
  const hasMoreResults = visibleResults.length > initialDisplayCount;
  const hiddenResultsCount = visibleResults.length - initialDisplayCount;

  return (
    <div className="mt-1">
      <div className="text-xs font-medium text-grayscale-900 mb-2">
        Found {visibleResults.length} result{visibleResults.length !== 1 ? 's' : ''} (sorted by relevance)
        {!showAllResults && hasMoreResults && (
          <span className="text-grayscale-700"> • Showing top {initialDisplayCount}</span>
        )}
      </div>
      
      {/* Responsive grid layout for mini cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedResults.map((result, index) => {
          if (result.source_type === 'technology') {
            return renderTechnologyCard(result, index);
          } else if (result.source_type === 'researcher') {
            return renderResearcherCard(result, index);
          }
          return null;
        }).filter(Boolean)}
      </div>

      {/* Show More/Show Less Button */}
      {hasMoreResults && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setShowAllResults(!showAllResults)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-100 hover:bg-primary-200 text-primary-og-600 font-grotesk text-sm font-medium rounded-full transition-colors border border-primary-300"
          >
            {showAllResults ? (
              <>
                <span>Show Less</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                <span>Show {hiddenResultsCount} More Result{hiddenResultsCount !== 1 ? 's' : ''}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
